/**
 * Token Refresh Module — Silent Refresh via Hidden Iframe
 *
 * Automatically renews Google ID Tokens before expiry using prompt=none.
 * Multi-tab safe via localStorage lock + storage event listener.
 *
 * No circular dependency: imports toastStore and svelte-i18n only,
 * manipulates localStorage directly instead of importing authStore.
 */

import { toastStore } from './stores/toastStore';
import { get } from 'svelte/store';
import { _ } from 'svelte-i18n';

// --- Constants ---

/** Refresh 5 minutes before token expiry */
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/** Iframe timeout — give up after 10 seconds */
const IFRAME_TIMEOUT_MS = 10 * 1000;

/** Minimum interval between refresh attempts (throttle) */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;

/** Lock key in localStorage for multi-tab coordination */
const LOCK_KEY = 'plc_refresh_lock';

/** Lock TTL — auto-release after 15 seconds */
const LOCK_TTL_MS = 15 * 1000;

/** State prefix for silent-refresh to distinguish from normal login */
const STATE_PREFIX = 'silent-refresh-';

// --- Module state ---

let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let lastRefreshAttempt = 0;
let stopped = false;

// --- Public API ---

/**
 * Decode the `exp` claim from a JWT token.
 * Returns the expiry as a Unix timestamp (seconds), or null if unparseable.
 */
export function getTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Milliseconds until token expiry. Returns 0 if already expired or unparseable.
 */
export function msUntilExpiry(token: string): number {
  const exp = getTokenExpiry(token);
  if (exp === null) return 0;
  const ms = exp * 1000 - Date.now();
  return Math.max(0, ms);
}

/**
 * Schedule the next silent refresh based on the current token in localStorage.
 * Safe to call multiple times — clears any existing timer first.
 */
export function scheduleTokenRefresh(): void {
  stopTokenRefresh();
  stopped = false;

  const token = localStorage.getItem('auth_token');
  if (!token) return;

  const ms = msUntilExpiry(token);
  if (ms === 0) return; // already expired

  const delay = Math.max(ms - REFRESH_BUFFER_MS, MIN_REFRESH_INTERVAL_MS);

  refreshTimer = setTimeout(() => {
    void silentRefresh();
  }, delay);

  // Listen for cross-tab token updates
  window.addEventListener('storage', onStorageChange);
}

/**
 * Stop any pending refresh timer and remove the storage listener.
 * Call this on logout.
 */
export function stopTokenRefresh(): void {
  stopped = true;
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  window.removeEventListener('storage', onStorageChange);
}

// --- Private helpers ---

/**
 * Handle storage events from other tabs.
 * If another tab updated auth_token, reschedule our timer.
 */
function onStorageChange(e: StorageEvent): void {
  if (e.key === 'auth_token') {
    if (e.newValue) {
      // Another tab got a new token — reschedule
      scheduleTokenRefresh();
    } else {
      // Token removed (logout) — stop
      stopTokenRefresh();
    }
  }
}

/**
 * Attempt a silent token refresh. Acquires a cross-tab lock first.
 */
async function silentRefresh(): Promise<void> {
  const now = Date.now();

  // Throttle
  if (now - lastRefreshAttempt < MIN_REFRESH_INTERVAL_MS) {
    scheduleTokenRefresh();
    return;
  }

  // Try to acquire lock (multi-tab)
  if (!acquireLock()) {
    // Another tab is refreshing — wait and reschedule
    scheduleTokenRefresh();
    return;
  }

  lastRefreshAttempt = now;

  try {
    const newToken = await refreshViaIframe();

    // Abort if stopTokenRefresh() was called while iframe was in-flight (e.g. logout)
    if (stopped) return;

    if (newToken) {
      updateToken(newToken);
    } else {
      onRefreshFailed();
    }
  } catch {
    if (!stopped) onRefreshFailed();
  } finally {
    releaseLock();
  }
}

/**
 * Create a hidden iframe that performs Google OAuth with prompt=none.
 * Returns the new id_token or null on failure/timeout.
 */
function refreshViaIframe(): Promise<string | null> {
  return new Promise((resolve) => {
    const clientId =
      window.__APP_CONFIG__?.googleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
    const redirectUri =
      window.__APP_CONFIG__?.oauthRedirectUri ||
      import.meta.env.VITE_OAUTH_REDIRECT_URI ||
      `${window.location.origin}/auth/callback`;

    const state = STATE_PREFIX + crypto.randomUUID();
    const nonce = crypto.randomUUID();

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'token id_token',
      scope: 'openid email profile',
      prompt: 'none',
      nonce,
      state,
    });

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');

    let resolved = false;

    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      window.removeEventListener('message', onMessage);
      clearTimeout(timeout);
      // Delay iframe removal to avoid navigation issues
      setTimeout(() => {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      }, 100);
    };

    const onMessage = (event: MessageEvent) => {
      // Only accept messages from our own origin
      if (event.origin !== window.location.origin) return;

      const data = event.data;
      if (!data || data.type !== 'silent-refresh-response') return;

      // Validate state matches
      if (data.state !== state) return;

      cleanup();
      resolve(data.token || null);
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve(null);
    }, IFRAME_TIMEOUT_MS);

    window.addEventListener('message', onMessage);

    iframe.src = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    document.body.appendChild(iframe);
  });
}

/**
 * Update localStorage and auth_user with the new token, then reschedule.
 */
function updateToken(newToken: string): void {
  localStorage.setItem('auth_token', newToken);

  // Update the stored user object with the new token
  const storedUser = localStorage.getItem('auth_user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);

      // Also update user info from new JWT claims
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      user.token = newToken;
      if (payload.email) user.email = payload.email;
      if (payload.name) user.name = payload.name;
      if (payload.picture) user.picture = payload.picture;

      localStorage.setItem('auth_user', JSON.stringify(user));
    } catch {
      // If user data is corrupted, at least the token is updated
    }
  }

  // Schedule next refresh
  scheduleTokenRefresh();
}

/**
 * Show a sticky warning toast when refresh fails.
 */
function onRefreshFailed(): void {
  const t = get(_);
  toastStore.warning(
    t('auth.session_expiring_soon'),
    0, // duration=0 → sticky, user must dismiss
  );

  // Don't reschedule — the existing 401 handler will do a hard logout if needed
}

// --- Multi-tab lock ---

function acquireLock(): boolean {
  const existing = localStorage.getItem(LOCK_KEY);
  if (existing) {
    const lockTime = parseInt(existing, 10);
    // If lock is still within TTL, another tab holds it
    if (Date.now() - lockTime < LOCK_TTL_MS) {
      return false;
    }
    // Lock expired — take over
  }

  localStorage.setItem(LOCK_KEY, Date.now().toString());
  return true;
}

function releaseLock(): void {
  localStorage.removeItem(LOCK_KEY);
}
